/**
 * I18n — Lightweight localization module for RoboGame.
 *
 * Usage:
 *   import { t, setLanguage, getLanguage } from '@/i18n/I18n';
 *   t('hud.wave', { n: 5, total: 12 }) → "Wave 5/12"
 *
 * Interpolation uses {{var}} placeholders.
 * Fallback chain: current lang → English → raw key.
 */

import { EventBus, GameEvents } from '@/events/EventBus';
import { SaveManager } from '@/systems/SaveManager';
import { en } from './en';
import { fr } from './fr';

export type Lang = 'en' | 'fr';

const TRANSLATIONS: Record<Lang, Record<string, string>> = { en, fr };

// Initialize from saved settings
let currentLang: Lang = (SaveManager.getSettings().language as Lang) || 'en';

/** Look up a translation key, with optional {{param}} interpolation. */
export function t(key: string, params?: Record<string, string | number>): string {
  const dict = TRANSLATIONS[currentLang];
  let str = dict?.[key] ?? TRANSLATIONS.en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
    }
  }
  return str;
}

/** Change the active language. Persists to localStorage and emits 'languageChanged'. */
export function setLanguage(lang: Lang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  SaveManager.updateSettings({ language: lang });
  EventBus.emit(GameEvents.LANGUAGE_CHANGED, lang);
}

/** Get the current language code. */
export function getLanguage(): Lang { return currentLang; }

/** Get all available languages. */
export function getAvailableLanguages(): Lang[] {
  return Object.keys(TRANSLATIONS) as Lang[];
}

/** Cycle to the next language (for toggle button). */
export function cycleLanguage(): Lang {
  const langs = getAvailableLanguages();
  const idx = langs.indexOf(currentLang);
  const next = langs[(idx + 1) % langs.length];
  setLanguage(next);
  return next;
}

/** Register runtime translations (e.g. custom cinematic dialogue text). */
export function addTranslations(lang: Lang, entries: Record<string, string>): void {
  Object.assign(TRANSLATIONS[lang], entries);
}

/** Read a single translation entry from the current language dict (undefined if not found). */
export function getTranslation(key: string): string | undefined {
  return TRANSLATIONS[currentLang]?.[key] ?? TRANSLATIONS.en[key];
}
